/**
 * Tour Context Provider
 * 
 * Manages in-app tour/tutorial system for guiding users through features.
 * Handles tour state, page-specific tour configurations, and user preferences
 * to allow skipping/canceling tours. Persists tour completion status.
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import logger from '@/shared/utils/logger.js';

const TourContext = createContext();

export const useTour = () => {
  const context = useContext(TourContext);
  if (!context) {
    throw new Error('useTour must be used within a TourProvider');
  }
  return context;
};

const TOUR_STORAGE_KEY = 'ismartpay_tour_preferences';
const DEFAULT_TOUR_SETTINGS = {
  enabled: true, // Global toggle for tours
  completedTours: [], // Array of tour IDs that have been completed
  dismissedTours: [] // Array of tour IDs that have been dismissed/skipped
};

export const TourProvider = ({ children }) => {
  const [isTourOpen, setIsTourOpen] = useState(false);
  const [currentTourId, setCurrentTourId] = useState(null);
  const [tourSettings, setTourSettings] = useState(DEFAULT_TOUR_SETTINGS);
  const location = useLocation();

  // Load tour preferences from localStorage on mount
  useEffect(() => {
    try {
      const saved = localStorage.getItem(TOUR_STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        setTourSettings({ ...DEFAULT_TOUR_SETTINGS, ...parsed });
      }
    } catch (error) {
      logger.error('Error loading tour preferences', { error });
    }
  }, []);

  // Save tour preferences to localStorage whenever they change
  useEffect(() => {
    try {
      localStorage.setItem(TOUR_STORAGE_KEY, JSON.stringify(tourSettings));
    } catch (error) {
      logger.error('Error saving tour preferences', { error });
    }
  }, [tourSettings]);

  // Check if a tour has been completed or dismissed
  const isTourCompletedOrDismissed = useCallback((tourId) => {
    return tourSettings.completedTours.includes(tourId) || 
           tourSettings.dismissedTours.includes(tourId);
  }, [tourSettings]);

  // Start a tour - always allow starting when manually triggered via button
  const startTour = useCallback((tourId, force = false) => {
    logger.debug('startTour called', { tourId, enabled: tourSettings.enabled, force });
    
    if (!tourSettings.enabled) {
      logger.warn('Tour is disabled in settings');
      return;
    }

    // If tour was previously dismissed/completed, remove it from the lists so it can restart
    // This allows users to restart tours anytime by clicking the help button
    if (isTourCompletedOrDismissed(tourId) || force) {
      logger.debug('Resetting tour state', { tourId });
      setTourSettings(prev => ({
        ...prev,
        dismissedTours: prev.dismissedTours.filter(id => id !== tourId),
        completedTours: prev.completedTours.filter(id => id !== tourId)
      }));
    }

    logger.debug('Starting tour', { tourId });
    // Set tour ID first, then open the tour after a small delay to ensure steps are loaded
    setCurrentTourId(tourId);
    // Use setTimeout to ensure the tour ID is set before opening
    setTimeout(() => {
      setIsTourOpen(true);
      logger.debug('Tour opened, isTourOpen set to true');
    }, 100);
  }, [tourSettings.enabled, isTourCompletedOrDismissed]);

  // Stop a tour (when user manually closes it)
  const stopTour = useCallback(() => {
    setIsTourOpen(false);
    if (currentTourId) {
      // Mark as dismissed when manually stopped
      setTourSettings(prev => ({
        ...prev,
        dismissedTours: prev.dismissedTours.includes(currentTourId)
          ? prev.dismissedTours
          : [...prev.dismissedTours, currentTourId]
      }));
    }
    setCurrentTourId(null);
  }, [currentTourId]);

  // Complete a tour (when user finishes all steps)
  const completeTour = useCallback((tourId) => {
    setIsTourOpen(false);
    setTourSettings(prev => ({
      ...prev,
      completedTours: prev.completedTours.includes(tourId)
        ? prev.completedTours
        : [...prev.completedTours, tourId]
    }));
    setCurrentTourId(null);
  }, []);

  // Reset tour completion/dismissal for a specific tour
  const resetTour = useCallback((tourId) => {
    setTourSettings(prev => ({
      ...prev,
      completedTours: prev.completedTours.filter(id => id !== tourId),
      dismissedTours: prev.dismissedTours.filter(id => id !== tourId)
    }));
  }, []);

  // Reset all tours
  const resetAllTours = useCallback(() => {
    setTourSettings({
      ...DEFAULT_TOUR_SETTINGS,
      enabled: tourSettings.enabled // Keep the enabled setting
    });
  }, [tourSettings.enabled]);

  // Toggle tours on/off globally
  const setToursEnabled = useCallback((enabled) => {
    setTourSettings(prev => ({ ...prev, enabled }));
  }, []);

  // Get tour ID from current route
  const getCurrentRouteTourId = useCallback(() => {
    const path = location.pathname;
    const routeToTourMap = {
      '/dashboard': 'dashboard-tour',
      '/disbursements': 'disbursements-tour',
      '/collections': 'collections-tour',
      '/checkout': 'checkout-tour',
      '/invoicing': 'invoicing-tour',
      '/payroll': 'payroll-tour',
      '/reports': 'reports-tour',
      '/utilities': 'utilities-tour',
      '/SMS': 'sms-tour',
      '/settlements': 'settlements-tour',
    };

    if (routeToTourMap[path]) {
      return routeToTourMap[path];
    }

    if (path.startsWith('/settings')) {
      return 'settings-tour';
    }

    return null;
  }, [location.pathname]);

  // Auto-start tour for new routes (optional - can be enabled/disabled)
  // Note: This is disabled by default - users can start tours manually via the help button
  // Uncomment the useEffect below if you want tours to auto-start on page load
  // useEffect(() => {
  //   const tourId = getCurrentRouteTourId();
  //   if (tourId && !isTourCompletedOrDismissed(tourId) && tourSettings.enabled) {
  //     const timer = setTimeout(() => {
  //       startTour(tourId);
  //     }, 1000);
  //     return () => clearTimeout(timer);
  //   }
  // }, [location.pathname, getCurrentRouteTourId, isTourCompletedOrDismissed, tourSettings.enabled, startTour]);

  const value = {
    isTourOpen,
    currentTourId,
    tourSettings,
    startTour,
    stopTour,
    completeTour,
    resetTour,
    resetAllTours,
    setToursEnabled,
    isTourCompletedOrDismissed,
    getCurrentRouteTourId
  };

  return (
    <TourContext.Provider value={value}>
      {children}
    </TourContext.Provider>
  );
};
